import Client from "data/_client"
import notificationService from "data/notification"
import folderService from "data/folder"
import propertyService from "data/property"
import { formatDate, parseDate } from "data/lib/dates"
import _ from "lodash"
import { local } from "./lib/local-json"
import toggles from 'feature-toggles'

// private module logic //

function toISOdate(date:any):string { // (00/00/0000 => 0000-00-00)
  if(date.indexOf("/") > -1){
    date = date.split('/');
    return date.length === 3 ? `${date[2]}-${date[0]}-${date[1]}` : `${date[1]}-${date[0]}-01`
  } else {
    return date
  }
}

function getCurrentFolder():object {
  let folders:object[] = [];
  folderService.stream().subscribe(data => { folders = data.value });
  const currentFolderId = _.last(JSON.parse(localStorage.getItem("folderPath")!));
  return _.find(folders, {folderid: currentFolderId});
}

function getDefaultScenario(prop?:object):object|null {
  const currentProperty = prop || propertyService.get();
  const valuationDetail = currentProperty['valuationdetail'];
  return _.find(valuationDetail, s => s['defaultScenario']);
}

function getReportBeginDefault(singleProperty:boolean, prop?:object|null, reportBegin?:boolean):string {
  const property = prop || propertyService.get()
  if (singleProperty){
    return reportBegin ? property['reportbegindate'] : getScenarioDefaultDate(property, getDefaultScenario(property));
  } else {
    let folder = getCurrentFolder();
    return folder['reportbegindate'];
  }
}

function getReportTermDefault(singleProperty:boolean, prop?:object|null):number {
  if (singleProperty){
    const currentProperty = prop || propertyService.get();
    const valuationDetail = currentProperty['valuationdetail'];
    const defaultScenario = _.find(valuationDetail, s => s.defaultScenario);
    const term = defaultScenario.term;
    return term
  } else {
    let folder = getCurrentFolder();
    return ((folder['term'] < 10) ? folder['term'] : 10);
  }
}

function getScenario(scenarioid: number, prop?:object|null): object {
  const currentProperty = prop || propertyService.get()
  const valuationDetail = currentProperty['valuationdetail']
  return _.find(valuationDetail, s => s.valuationPeriod === scenarioid)
}

function getScenarioTerm(scenarioid: number): number {
  const scenario = getScenario(scenarioid)
  const term = _.get(scenario, "term")
  return term
}

function getScenarioBeginDate(scenarioid: number): string {
  return getScenarioDefaultDate(propertyService.get(), getScenario(scenarioid));
}

function getScenarioDefaultDate(currentProperty:object, scenario:object|null){
  if (scenario){
    switch(scenario['valuationDateType']){
      case 'Report Begin': return currentProperty['reportbegindate'];
      case 'Analysis Begin': return currentProperty['analysisbegindate'];
      case 'Specified Date': return scenario['valuationDate'];
    }
  } else {
    return currentProperty['reportbegindate']
  }
}

function getUnderwritingTermMax(property: object, scenarioid: number, beginDate?: string): number {
  const valuationScenario = getScenario(scenarioid)
  const analysisBegin = parseDate(property['analysisbegindate']);
  const valuationDate = parseDate(beginDate || _.get(valuationScenario, "valuationDate"));
  const propertyTerm = property['term']

  let maxTerm = Math.floor(
    (12 * (analysisBegin.year() - valuationDate.year() + propertyTerm) +
      (analysisBegin.month() + 1 - (valuationDate.month() + 1))) /
    12
  )
  if (maxTerm > 20) maxTerm = 20;
  if (maxTerm < 1) maxTerm = 1;

  return maxTerm;
}

function validateTerm(term: number, property: object, scenarioid: number, beginDate?: string): number {
  const max = getUnderwritingTermMax(property, scenarioid, beginDate);

  if (term < 3){
    notificationService.notify({
      type: "warn",
      message: "Valuation Term adjusted to the minimum term of 3",
      dismiss: 5000
    })
    return 3
  } else if (term > max){
    notificationService.notify({
      type: "warn",
      message: "Valuation Term adjusted to valuation term max",
      dismiss: 5000
    })
    return max
  } else {
    return term
  }
}

function getUnderwritingMaxDate(property: object, term?: number): string {
  const analysisBegin     = parseDate(property['analysisbegindate']);
  const propertyTerm      = term || property['term'];
  const analysisBeginDate = parseDate(analysisBegin);

  return `${analysisBeginDate.add(propertyTerm, 'years').subtract(1,'year').toDate()}`
}

function validateBeginDate(beginDate: string, property: object, term?: number): string {
  const analysisBegin         = parseDate(property['analysisbegindate']);
  const underwritingDateMax   = getUnderwritingMaxDate(property, term);

  if (parseDate(beginDate).isBefore(parseDate(analysisBegin))){
    notificationService.notify({
      type: "warn",
      message: "Valuation Date adjusted to the analysis begin date",
      dismiss: 5000
    })
    return formatDate(analysisBegin)
  } else if (parseDate(beginDate).isAfter(parseDate(underwritingDateMax))){
    notificationService.notify({
      type: "warn",
      message: "Valuation Date adjusted to the underwriting date max",
      dismiss: 5000
    })
    return formatDate(underwritingDateMax)
  } else {
    return formatDate(beginDate)
  }
}

function quickReport(targetPropId?: number|null, beginDate?: string, term?: number|null, scenarioid?: number|null, properties?: number[]) {
  const profile = JSON.parse(localStorage.getItem("auth.profile")!);
  const currentPropId = targetPropId ? targetPropId : JSON.parse(localStorage.getItem("property")!);
  const currentProperty = targetPropId ? propertyService.get(targetPropId) : _.get(properties, "length") === 1 ? propertyService.get(_.get(properties, "[0]")) : propertyService.get();
  const currentFolderId = folderService.getCurrentFolderId();
  const currentFolder = _.get(properties, "length") > 1 ? folderService.get(currentFolderId) : null
  if(_.get(currentProperty, "type") === "Hospitality" && !properties) {
    notificationService.notify({ type: "warn", message: "Underwriting workbook does not support hospitality properties", dismiss: 2000 })
    return
  }

  const currentScenario = scenarioid ? getScenario(scenarioid, currentProperty) : null
  const defaultScenario = _.find(currentProperty?.valuationdetail, s => s.defaultScenario);

  if (!currentPropId && !properties){
    notificationService.notify({ type: "warn", message: "You must select a property before continuing.", dismiss: 2000 });
  } else if (_.get(currentProperty, "type") == 'Hospitality' && !properties) {
    notificationService.notify({ type: "warn", message: "Underwriting workbook does not support hospitality properties", dismiss: 2000 });
  } else if (!toggles.allowMultifamilyUW() && local('property_type', null) == 'Multifamily'){
    notificationService.notify({ type: "warn", message: "Not currently supported for multifamily properties", dismiss: 2000 });
  } else if (!properties && !_.includes(['Calculated', 'Calculated w/Errors'], _.get(currentProperty, ['calcdetail', 'status']))){
    notificationService.notify({ type: "warn", message: "Property must be calculated to run report", dismiss: 2000 });
  } else if (currentScenario && currentScenario['valuationType'] == 'Direct Cap'){
    notificationService.notify({ type: "warn", message: "Underwriting workbook does not support the Direct Cap Valuation Type.", dismiss: 2000 });
  } else if (!scenarioid && !properties && defaultScenario['valuationType'] == 'Direct Cap'){
    notificationService.notify({ type: "warn", message: "Underwriting workbook does not support the Direct Cap Valuation Type.", dismiss: 2000 });
  } else if (profile.email_verified !== true) {
    notificationService.notify({ type: "warn", message: "You will need to verify your email before receiving reports!", dismiss: 2000 });
  } else {
    let payload = {
      portfolio: _.get(properties, "length") > 1 ? true : false,
      propertyid: properties || [currentPropId],
      beginDate: beginDate || toISOdate(getReportBeginDefault(true, currentProperty)),
      modelTerm: term || getReportTermDefault(true, currentProperty)
    }
    if (!properties) payload['assetType'] = _.get(currentProperty, "type") == 'Multifamily' ? 'Multifamily' : 'Commercial'
    if(scenarioid && !properties) payload['valuationPeriod'] = scenarioid
    else if(defaultScenario && !properties){
      let defaultScenario = getDefaultScenario(currentProperty);
      payload['valuationPeriod'] = defaultScenario?.['valuationPeriod'];
    }

    return Client.post(`report/request_uw_workbook`, payload)
    .then((res: string) => {
      if (res === 'message accepted'){
        notificationService.notify({ type: "info", message: "You have initiated a new underwriting workbook", dismiss: 2000 });
        notificationService.enqueue({
          message: currentFolder ? `Underwriting started for folder: ${currentFolder.code}` : `Underwriting started for property: ${currentProperty.code}`,
          correlationid: currentFolder ? `underwriting-folder-${currentFolder.folderid}` : `underwriting-property-${currentProperty.propertyid}`,
          longRunning: true
        })
      }
    })
    .catch((err: {message: string}) => {
      notificationService.notify({ type: "danger", message: "Uh-oh! It looks like an error has occurred.", dismiss: 2000 });
      notificationService.enqueue({
        message: currentFolder ? `Underwriting folder error` : `Underwriting property error`,
        correlationid: currentFolder ? `underwriting-folder-${currentFolder.folderid}` : `underwriting-property-${currentProperty.propertyid}`,
        complete: true
      })
    });
  }
}

async function getWorkbooks() {
  return await Client.get(`report/underwritingworkbook`).then((res) => res.json());
}

async function downloadWorkbook(workbook:object) {
  const res = await Client.get(`report/underwritingworkbook/${workbook['filelocation']}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob)
}

async function getWorkbook(workbook:object){
  const url = await downloadWorkbook(workbook);
  const filename = workbook['filename']
  saveFile(filename, url);
}

function saveFile (name:string, url:any){
  let a = document.createElement('a');
  a.setAttribute('href', url);
  a.setAttribute('download', name);
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
}

async function deleteWorkbook(workbook:object){
  return await Client.del(`report/underwritingworkbook/${workbook['filelocation']}`)
}

// public module interface //

const underwritingService = {
  deleteWorkbook,
  getDefaultScenario,
  getReportBeginDefault,
  getReportTermDefault,
  getScenarioBeginDate,
  getScenarioTerm,
  getWorkbooks,
  getWorkbook,
  quickReport,
  validateBeginDate,
  validateTerm,
  toISOdate
}

export default underwritingService
